/**
 * Comprehensive motor rating (Proposed Motor Pricing).
 * All TPO-eligible portal classes. Period is always 1 Month; customer pays 30% of annual total.
 * Optional benefits use agent-entered insured values (where the schedule allows).
 */

const COVER_TYPE = 'COMP';
const COVER_PERIOD = '1 Month';
const PAYABLE_FACTOR = 0.3;
/** Strict product rule: all COMP classes require SI >= 500,000. */
const COMP_GLOBAL_MIN_SUM_INSURED = 500000;
const MAX_VEHICLE_AGE_YEARS = 15;

const FREE_30K = { freeLimit: 30000, rateAboveFree: 0.1 };
const FREE_50K = { freeLimit: 50000, rateAboveFree: 0.1 };
const FREE_10K = { freeLimit: 10000, rateAboveFree: 0.1 };

const EP_PRIVATE = { rate: 0.0025, minimum: 3000 };
const EP_COMMERCIAL = { rate: 0.005, minimum: 5000 };
const EP_PSV = { rate: 0.005, minimum: 10000 };
const PVT_PRIVATE = { rate: 0.0025, minimum: 2500 };
const PVT_COMMERCIAL = { rate: 0.0035, minimum: 3500 };

function bands4(r1, r2, r3, r4) {
  return [
    { min: 500000, max: 1500000, rate: r1 },
    { min: 1500001, max: 2000000, rate: r2 },
    { min: 2000001, max: 2500000, rate: r3 },
    { min: 2500001, max: Infinity, rate: r4 }
  ];
}

function flatFrom(minSi, rate) {
  return [{ min: minSi, max: Infinity, rate }];
}

/**
 * Rate schedule from Proposed Motor Pricing.pdf, keyed by portal TPO class names.
 */
const CLASS_SCHEDULE = Object.freeze({
  'Motor Private': {
    minSumInsured: 500000,
    minBasicPremium: 27500,
    basicBands: bands4(0.04, 0.0375, 0.035, 0.03),
    excessProtector: EP_PRIVATE,
    pvt: PVT_PRIVATE,
    windscreen: FREE_30K,
    radio: FREE_30K
  },
  'Motor Commercial Own Goods': {
    minSumInsured: 500000,
    minBasicPremium: 30000,
    basicBands: bands4(0.04, 0.04, 0.0375, 0.04),
    excessProtector: EP_COMMERCIAL,
    pvt: PVT_COMMERCIAL,
    windscreen: FREE_50K,
    radio: FREE_50K
  },
  'Motor Commercial General Cartage': {
    minSumInsured: 500000,
    minBasicPremium: 30000,
    basicBands: bands4(0.04, 0.04, 0.04, 0.0375),
    excessProtector: EP_COMMERCIAL,
    pvt: PVT_COMMERCIAL,
    windscreen: FREE_50K,
    radio: FREE_50K
  },
  'Motor Commercial Institutional': {
    minSumInsured: 500000,
    minBasicPremium: 30000,
    basicBands: bands4(0.04, 0.0375, 0.035, 0.03),
    excessProtector: EP_COMMERCIAL,
    pvt: PVT_COMMERCIAL,
    windscreen: FREE_50K,
    radio: FREE_50K
  },
  // TPO "Motor Commercial" (TOR naming) → Own Goods COMP schedule
  'Motor Commercial': {
    minSumInsured: 500000,
    minBasicPremium: 30000,
    basicBands: bands4(0.04, 0.04, 0.0375, 0.04),
    excessProtector: EP_COMMERCIAL,
    pvt: PVT_COMMERCIAL,
    windscreen: FREE_50K,
    radio: FREE_50K
  },
  // Agriculture & Forestry sheet → Tractor TPO portal class
  'Tractor TPO': {
    minSumInsured: 500000,
    minBasicPremium: 30000,
    basicBands: [
      { min: 500000, max: 1500000, rate: 0.035 },
      { min: 1500001, max: 2000000, rate: 0.0325 },
      { min: 2000001, max: Infinity, rate: 0.03 }
    ],
    excessProtector: EP_COMMERCIAL,
    pvt: PVT_COMMERCIAL,
    windscreen: FREE_50K,
    radio: FREE_50K
  },
  // Prime Movers share commercial cartage-style COMP rating (same TPO block family as 0807)
  'Prime Movers': {
    minSumInsured: 500000,
    minBasicPremium: 30000,
    basicBands: bands4(0.04, 0.04, 0.04, 0.0375),
    excessProtector: EP_COMMERCIAL,
    pvt: PVT_COMMERCIAL,
    windscreen: FREE_50K,
    radio: FREE_50K
  },
  'Tuk Tuk Commercial': {
    minSumInsured: 500000,
    minBasicPremium: 10000,
    basicBands: flatFrom(200000, 0.04),
    excessProtector: EP_COMMERCIAL,
    pvt: PVT_COMMERCIAL,
    windscreen: FREE_10K,
    radio: FREE_10K
  },
  'Tuk Tuk PSV': {
    minSumInsured: 500000,
    minBasicPremium: 10000,
    basicBands: flatFrom(200000, 0.05),
    excessProtector: EP_COMMERCIAL,
    pvt: PVT_COMMERCIAL,
    windscreen: FREE_10K,
    radio: FREE_10K
  },
  // All motorcycles: basic Comprehensive only — no Excess Protector / PVT / windscreen / radio
  'Motorcycle Private': {
    minSumInsured: 500000,
    minBasicPremium: 5000,
    basicBands: flatFrom(80000, 0.03),
    excessProtector: null,
    pvt: null,
    windscreen: null,
    radio: null,
    noAddons: true
  },
  'Motorcycle PSV Annual': {
    minSumInsured: 500000,
    minBasicPremium: 6000,
    basicBands: flatFrom(80000, 0.04),
    excessProtector: null,
    pvt: null,
    windscreen: null,
    radio: null,
    noAddons: true
  },
  'Motorcycle PSV 6 Months': {
    minSumInsured: 500000,
    minBasicPremium: 6000,
    basicBands: flatFrom(80000, 0.04),
    excessProtector: null,
    pvt: null,
    windscreen: null,
    radio: null,
    noAddons: true
  },
  'Motorcycle PSV': {
    minSumInsured: 500000,
    minBasicPremium: 6000,
    basicBands: flatFrom(80000, 0.04),
    excessProtector: null,
    pvt: null,
    windscreen: null,
    radio: null,
    noAddons: true
  },
  'PSV Chauffer Driven': {
    minSumInsured: 500000,
    minBasicPremium: 35000,
    basicBands: flatFrom(500000, 0.055),
    excessProtector: EP_PSV,
    pvt: PVT_COMMERCIAL,
    windscreen: FREE_30K,
    radio: FREE_30K
  }
});

/** All TPO portal classes + common aliases present in CLASS_SCHEDULE. */
const ENABLED_VEHICLE_CLASSES = Object.freeze(Object.keys(CLASS_SCHEDULE));

const CLASS_ALIASES = Object.freeze({
  'motor cycle': 'Motorcycle Private',
  motorcycle: 'Motorcycle Private',
  'motor cycle private': 'Motorcycle Private',
  'motorcycle psv boda': 'Motorcycle PSV Annual',
  'psv chauffeur driven': 'PSV Chauffer Driven',
  'motor psv chauffer driven': 'PSV Chauffer Driven',
  'tuktuk commercial': 'Tuk Tuk Commercial',
  'tuktuk psv': 'Tuk Tuk PSV',
  tractor: 'Tractor TPO'
});

function money(n) {
  return Math.round(Number(n) * 100) / 100;
}

function normalizeVehicleClass(vehicleClass) {
  return String(vehicleClass || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/chauffeur/gi, 'Chauffer');
}

function titleCaseClass(vehicleClass) {
  const n = normalizeVehicleClass(vehicleClass);
  const lower = n.toLowerCase();
  if (CLASS_ALIASES[lower]) return CLASS_ALIASES[lower];
  const found = ENABLED_VEHICLE_CLASSES.find((c) => c.toLowerCase() === lower);
  return found || n;
}

function parsePositiveAmount(value, fieldName) {
  if (value == null || value === '') return 0;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    const err = new Error(`${fieldName} must be a non-negative number`);
    err.code = 'COMP_INVALID_AMOUNT';
    throw err;
  }
  return money(n);
}

function vehicleAgeYears(yearOfManufacture, asOfYear = new Date().getFullYear()) {
  const yom = parseInt(yearOfManufacture, 10);
  if (!Number.isFinite(yom) || yom < 1900 || yom > asOfYear + 1) {
    const err = new Error('yearOfManufacture is required and must be a valid year');
    err.code = 'COMP_INVALID_YEAR';
    throw err;
  }
  return asOfYear - yom;
}

function assertVehicleAgeEligible(yearOfManufacture, asOfYear) {
  const age = vehicleAgeYears(yearOfManufacture, asOfYear);
  if (age > MAX_VEHICLE_AGE_YEARS) {
    const err = new Error(
      `Vehicle age ${age} years exceeds Comprehensive maximum of ${MAX_VEHICLE_AGE_YEARS} years`
    );
    err.code = 'COMP_VEHICLE_TOO_OLD';
    err.vehicleAge = age;
    throw err;
  }
  return age;
}

function resolveBasicRate(schedule, sumInsured) {
  const band = schedule.basicBands.find((b) => sumInsured >= b.min && sumInsured <= b.max);
  if (!band) {
    const err = new Error(
      `Sum insured ${sumInsured} is outside Comprehensive rating bands (min ${schedule.minSumInsured})`
    );
    err.code = 'COMP_SI_OUT_OF_BAND';
    throw err;
  }
  return band;
}

function ratePercentWithMinimum(value, cfg) {
  if (!cfg || !value || value <= 0) return 0;
  return money(Math.max(value * cfg.rate, cfg.minimum));
}

function rateAboveFreeLimit(value, cfg) {
  if (!cfg || !value || value <= 0) return 0;
  if (value <= cfg.freeLimit) return 0;
  return money((value - cfg.freeLimit) * cfg.rateAboveFree);
}

function assertBenefitAllowed(name, selected, cfg) {
  if (selected && !cfg) {
    const err = new Error(`${name} is not available for this Comprehensive vehicle class`);
    err.code = 'COMP_BENEFIT_NOT_AVAILABLE';
    throw err;
  }
}

/** EP / PVT are opt-in flags (not free-form amounts). Rate against vehicle sum insured. */
function isOptInFlag(value) {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes' || s === 'on';
  }
  // Legacy: numeric amount > 0 treated as selected (still rated on SI, not that amount)
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return true;
  return false;
}

function formatBandLabel(band) {
  const minLabel = band.min.toLocaleString('en-KE');
  if (band.max === Infinity) return `${minLabel} and above`;
  return `${minLabel} – ${band.max.toLocaleString('en-KE')}`;
}

/**
 * @param {object} input
 * @param {string} input.vehicleClass
 * @param {number|string} input.sumInsured
 * @param {number|string} input.yearOfManufacture
 * @param {object} [input.benefits]
 * @param {boolean|string|number} [input.benefits.excessProtector] opt-in; rated as % of SI
 * @param {boolean|string|number} [input.benefits.pvt] opt-in; rated as % of SI
 * @param {number|string} [input.benefits.windscreen] declared limit
 * @param {number|string} [input.benefits.radio] declared limit
 * @param {number} [input.asOfYear]
 */
function calculateComprehensivePremium(input = {}) {
  const vehicleClass = titleCaseClass(input.vehicleClass);
  if (!ENABLED_VEHICLE_CLASSES.some((c) => c.toLowerCase() === vehicleClass.toLowerCase())) {
    const err = new Error(
      `Comprehensive class not supported. Enabled: ${ENABLED_VEHICLE_CLASSES.join(', ')}`
    );
    err.code = 'COMP_CLASS_NOT_ENABLED';
    throw err;
  }

  const schedule = CLASS_SCHEDULE[vehicleClass];
  if (!schedule) {
    const err = new Error(`No Comprehensive schedule configured for ${vehicleClass}`);
    err.code = 'COMP_NO_SCHEDULE';
    throw err;
  }

  const sumInsured = parsePositiveAmount(input.sumInsured, 'sumInsured');
  const effectiveMinSi = Math.max(schedule.minSumInsured || 0, COMP_GLOBAL_MIN_SUM_INSURED);
  if (sumInsured < effectiveMinSi) {
    const err = new Error(
      `Sum insured must be at least KES ${effectiveMinSi.toLocaleString('en-KE')}`
    );
    err.code = 'COMP_SI_TOO_LOW';
    throw err;
  }

  const vehicleAge = assertVehicleAgeEligible(input.yearOfManufacture, input.asOfYear);
  const band = resolveBasicRate(schedule, sumInsured);
  const basicCalculated = money(sumInsured * band.rate);
  const basicPremium = money(Math.max(basicCalculated, schedule.minBasicPremium));

  const benefitsIn = input.benefits || {};
  const excessProtectorSelected = isOptInFlag(benefitsIn.excessProtector);
  const pvtSelected = isOptInFlag(benefitsIn.pvt);
  const windscreenValue = parsePositiveAmount(benefitsIn.windscreen, 'windscreen');
  const radioValue = parsePositiveAmount(benefitsIn.radio, 'radio');

  if (schedule.noAddons) {
    const anyAddon =
      excessProtectorSelected || pvtSelected || windscreenValue > 0 || radioValue > 0;
    if (anyAddon) {
      const err = new Error(
        'Motorcycle Comprehensive has no add-ons (no Excess Protector, PVT, Windscreen, or Radio). Basic cover only.'
      );
      err.code = 'COMP_MOTORCYCLE_NO_ADDONS';
      throw err;
    }
  }

  assertBenefitAllowed('Excess Protector', excessProtectorSelected, schedule.excessProtector);
  assertBenefitAllowed('PVT', pvtSelected, schedule.pvt);
  assertBenefitAllowed('Windscreen', windscreenValue > 0, schedule.windscreen);
  assertBenefitAllowed('Radio', radioValue > 0, schedule.radio);

  const excessProtectorPremium = excessProtectorSelected
    ? ratePercentWithMinimum(sumInsured, schedule.excessProtector)
    : 0;
  const pvtPremium = pvtSelected ? ratePercentWithMinimum(sumInsured, schedule.pvt) : 0;
  const windscreenPremium = rateAboveFreeLimit(windscreenValue, schedule.windscreen);
  const radioPremium = rateAboveFreeLimit(radioValue, schedule.radio);

  const annualTotal = money(
    basicPremium + excessProtectorPremium + pvtPremium + windscreenPremium + radioPremium
  );
  const payableAmount = money(annualTotal * PAYABLE_FACTOR);

  return {
    coverType: COVER_TYPE,
    coverPeriod: COVER_PERIOD,
    payableFactor: PAYABLE_FACTOR,
    vehicleClass,
    sumInsured,
    yearOfManufacture: parseInt(input.yearOfManufacture, 10),
    vehicleAge,
    maxVehicleAgeYears: MAX_VEHICLE_AGE_YEARS,
    basic: {
      bandRate: band.rate,
      bandLabel: formatBandLabel(band),
      calculated: basicCalculated,
      minimum: schedule.minBasicPremium,
      premium: basicPremium
    },
    benefits: {
      excessProtector: {
        selected: excessProtectorSelected,
        ratedOn: excessProtectorSelected ? 'sumInsured' : null,
        rate: schedule.excessProtector ? schedule.excessProtector.rate : null,
        minimum: schedule.excessProtector ? schedule.excessProtector.minimum : null,
        premium: excessProtectorPremium,
        available: !!schedule.excessProtector
      },
      pvt: {
        selected: pvtSelected,
        ratedOn: pvtSelected ? 'sumInsured' : null,
        rate: schedule.pvt ? schedule.pvt.rate : null,
        minimum: schedule.pvt ? schedule.pvt.minimum : null,
        premium: pvtPremium,
        available: !!schedule.pvt
      },
      windscreen: {
        value: windscreenValue,
        freeLimit: schedule.windscreen ? schedule.windscreen.freeLimit : null,
        premium: windscreenPremium,
        withinFreeLimit:
          windscreenValue > 0 &&
          schedule.windscreen &&
          windscreenValue <= schedule.windscreen.freeLimit,
        chargeableAboveFree: Math.max(
          0,
          schedule.windscreen ? windscreenValue - schedule.windscreen.freeLimit : 0
        ),
        available: !!schedule.windscreen
      },
      radio: {
        value: radioValue,
        freeLimit: schedule.radio ? schedule.radio.freeLimit : null,
        premium: radioPremium,
        withinFreeLimit:
          radioValue > 0 && schedule.radio && radioValue <= schedule.radio.freeLimit,
        chargeableAboveFree: Math.max(
          0,
          schedule.radio ? radioValue - schedule.radio.freeLimit : 0
        ),
        available: !!schedule.radio
      }
    },
    annualTotal,
    payableAmount,
    addonsAllowed: !schedule.noAddons,
    notes: [
      'Period is strictly 1 Month',
      'Customer pays 30% of annual total (basic + selected benefits)',
      'Excess Protector and PVT are opt-in and rated as a % of vehicle sum insured (with schedule minimum)',
      'Windscreen and Radio use agent-declared limits',
      'Stamp duty / levies not included in this phase',
      'No DMVIC / Core in internal phase',
      'All TPO portal classes are rate-enabled',
      ...(schedule.noAddons
        ? ['Motorcycle classes: basic Comprehensive only — no Excess Protector or other add-ons']
        : [])
    ]
  };
}

module.exports = {
  COVER_TYPE,
  COVER_PERIOD,
  PAYABLE_FACTOR,
  MAX_VEHICLE_AGE_YEARS,
  COMP_GLOBAL_MIN_SUM_INSURED,
  ENABLED_VEHICLE_CLASSES,
  CLASS_SCHEDULE,
  calculateComprehensivePremium,
  normalizeVehicleClass,
  titleCaseClass,
  vehicleAgeYears,
  assertVehicleAgeEligible
};
